import { type Component, type JSX, Show, createSignal } from 'solid-js'
import { Portal } from 'solid-js/web'

interface TooltipProps {
  content: JSX.Element
  position?: 'top' | 'bottom'
  // 'light' matches the chart tooltips (white card, gray border); content handles its own padding.
  variant?: 'dark' | 'light'
  children: JSX.Element
}

const Tooltip: Component<TooltipProps> = (props) => {
  const [visible, setVisible] = createSignal(false)
  const [coords, setCoords] = createSignal({ top: 0, left: 0 })
  let triggerRef!: HTMLDivElement
  const gap = 8
  const isLight = () => props.variant === 'light'

  const updatePosition = () => {
    const rect = triggerRef.getBoundingClientRect()
    const pos = props.position ?? 'bottom'

    setCoords({
      left: rect.left + rect.width / 2,
      top: pos === 'bottom' ? rect.bottom + gap : rect.top - gap
    })
  }

  const show = () => {
    updatePosition()
    setVisible(true)
  }

  const hide = () => setVisible(false)

  return (
    <div ref={triggerRef} class="relative inline-flex" onMouseEnter={show} onMouseLeave={hide}>
      {props.children}
      <Portal>
        <div
          class="pointer-events-none fixed z-50 w-max max-w-xs rounded-lg transition-opacity duration-150"
          classList={{
            'invisible opacity-0': !visible(),
            'visible opacity-100': visible(),
            'bg-gray-900 px-3 py-2 text-xs text-white shadow-lg': !isLight(),
            'overflow-hidden border border-gray-200 bg-white text-gray-900 shadow-md': isLight()
          }}
          style={{
            left: `${coords().left}px`,
            top: `${coords().top}px`,
            transform: (props.position ?? 'bottom') === 'bottom' ? 'translateX(-50%)' : 'translate(-50%, -100%)'
          }}
          role="tooltip"
        >
          {props.content}
          <Show when={!isLight()}>
            <div
              class="absolute left-1/2 h-2 w-2 -translate-x-1/2 rotate-45 bg-gray-900"
              classList={{
                '-top-1': (props.position ?? 'bottom') === 'bottom',
                '-bottom-1': props.position === 'top'
              }}
            />
          </Show>
        </div>
      </Portal>
    </div>
  )
}

export default Tooltip
